import React, { ChangeEvent, useState } from 'react';
import TextField from '@mui/material/TextField';
import { InputAdornment, IconButton } from '@mui/material';
import {
  CheckOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Box, Button, Card, Chip, Typography, useTheme } from '@mui/material';
import {
  validateConfirmPassword,
  validateDigit,
  validateLowerCase,
  validateMinLength,
  validateSpecialCharacter,
  validateUpperCase,
} from '../../utils/utils';
import { ProfileFieldProps } from '../types';
import { FormattedMessage, useIntl } from 'react-intl';

type PasswordFieldProps = ProfileFieldProps & {
  onClickNext: () => void;
};

/**
 * Displays the password and confirm password fields.
 */
export const PasswordField = ({
  profile,
  setProfile,
  onClickNext,
}: PasswordFieldProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasChangedConfirmPassword, setHasChangedConfirmPassword] = useState(
    profile.confirmPassword != '',
  );

  // Validates the password and the confirm password fields.
  const hasValidConfirmPassword = validateConfirmPassword(
    profile.password,
    profile.confirmPassword,
  );
  const hasMinLength = validateMinLength(profile.password);
  const hasUpperCase = validateUpperCase(profile.password);
  const hasLowerCase = validateLowerCase(profile.password);
  const hasDigit = validateDigit(profile.password);
  const hasSpecialCharacter = validateSpecialCharacter(profile.password);

  const isValidPassword =
    hasValidConfirmPassword &&
    hasMinLength &&
    hasUpperCase &&
    hasLowerCase &&
    hasDigit &&
    hasSpecialCharacter;

  // The handler for the password visibility icon.
  const handleShowPassword = () => setShowPassword((show) => !show);

  // The handler for the confirm password visibility icon.
  const handleShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  // The handler for the password field.
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProfile({
      ...profile,
      password: event.target.value,
    });
  };

  // The handler for the confirm password field.
  const handleConfirmPasswordChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    if (!hasChangedConfirmPassword) setHasChangedConfirmPassword(true);
    setProfile({
      ...profile,
      confirmPassword: event.target.value,
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <span className="titleSmall">
          <FormattedMessage
            id="welcome"
            values={{
              email: profile.email,
              code: (word) => <Chip label={word} />,
            }}
          />
        </span>
      </div>

      <div>
        <TextField
          fullWidth
          data-testid="password-input"
          required
          sx={{
            marginBottom: '16px',
          }}
          InputLabelProps={{
            style: {
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: '2px',
            },
          }}
          id="password"
          label={intl.formatMessage({ id: 'password' })}
          type={showPassword ? 'text' : 'password'}
          value={profile.password}
          InputProps={{
            style: {
              paddingRight: '16px',
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPassword}
                  edge="end"
                >
                  {showPassword ? (
                    <VisibilityOffOutlined />
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={handlePasswordChange}
        ></TextField>
      </div>
      <div>
        <TextField
          fullWidth
          data-testid="confirm-password-input"
          required
          sx={{
            marginBottom: '8px',
          }}
          InputLabelProps={{
            style: {
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: '2px',
            },
          }}
          id="confirm_password"
          label={intl.formatMessage({ id: 'confirm_password' })}
          value={profile.confirmPassword}
          type={showConfirmPassword ? 'text' : 'password'}
          InputProps={{
            style: {
              paddingRight: '16px',
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={handleShowConfirmPassword}
                  edge="end"
                >
                  {showConfirmPassword ? (
                    <VisibilityOffOutlined />
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={handleConfirmPasswordChange}
          error={hasChangedConfirmPassword && !hasValidConfirmPassword}
          helperText={
            hasChangedConfirmPassword && !hasValidConfirmPassword
              ? intl.formatMessage({ id: 'confirm_password.error' })
              : ''
          }
        ></TextField>
      </div>
      <Card
        sx={{
          padding: '8px',
          marginBottom: '8px',
        }}
        elevation={0}
      >
        <Box display="flex" sx={{ gap: '4px' }}>
          <CheckOutlined
            style={{
              color: hasMinLength
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          />
          <Typography
            style={{
              color: hasMinLength
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          >
            {intl.formatMessage({ id: 'password_requirements.length' })}
          </Typography>
        </Box>
        <Box display="flex" sx={{ gap: '4px' }}>
          <CheckOutlined
            style={{
              color: hasUpperCase
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          />
          <Typography
            style={{
              color: hasUpperCase
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          >
            {intl.formatMessage({ id: 'password_requirements.uppercase' })}
          </Typography>
        </Box>
        <Box display="flex" sx={{ gap: '4px' }}>
          <CheckOutlined
            style={{
              color: hasLowerCase
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          />
          <Typography
            style={{
              color: hasLowerCase
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          >
            {intl.formatMessage({ id: 'password_requirements.lowercase' })}
          </Typography>
        </Box>
        <Box display="flex" sx={{ gap: '4px' }}>
          <CheckOutlined
            style={{
              color: hasDigit
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          />
          <Typography
            style={{
              color: hasDigit
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          >
            {intl.formatMessage({ id: 'password_requirements.number' })}
          </Typography>
        </Box>
        <Box display="flex" sx={{ gap: '4px' }}>
          <CheckOutlined
            style={{
              color: hasSpecialCharacter
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          />
          <Typography
            align="left"
            style={{
              color: hasSpecialCharacter
                ? theme.palette.success.main
                : theme.palette.text.disabled,
            }}
          >
            {intl.formatMessage({
              id: 'password_requirements.special_character',
            })}
          </Typography>
        </Box>
      </Card>
      <Card elevation={0}>
        <div>
          <Button
            fullWidth
            disableElevation
            disabled={!isValidPassword}
            variant="contained"
            sx={{
              textTransform: 'capitalize',
              '&.Mui-disabled': {
                background: '#2864AC',
              },
            }}
            onClick={() => {
              onClickNext();
            }}
          >
            {intl.formatMessage({
              id: 'next',
            })}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default PasswordField;
